import { Component } from '@theme/component';

/**
 * Snug Whiskey Fest — Check-In App Component
 *
 * Host-facing mobile-first interface for event check-in.
 * PIN auth → event select → grouped attendee list → check-in toggles → notes.
 */
class SwfCheckinComponent extends Component {
  #apiUrl = '';
  #authToken = null;
  #attendees = [];
  #currentEventId = null;

  connectedCallback() {
    super.connectedCallback();
    this.#apiUrl = (this.dataset.apiUrl || '').replace(/\/$/, '');

    // Restore session if available
    const saved = sessionStorage.getItem('swf_checkin_token');
    if (saved) {
      this.#authToken = saved;
      this.#showMain();
      this.#loadEvents();
    }
  }

  // ── Authentication ──

  async handlePinSubmit(event) {
    event.preventDefault();
    const pin = this.refs.pinInput?.value;
    const error = this.refs.pinError;

    if (!pin) return;

    try {
      const res = await fetch(`${this.#apiUrl}/checkin/auth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin }),
      });

      const data = await res.json();

      if (!res.ok) {
        if (error) {
          error.textContent = data.error || 'Invalid PIN';
          error.hidden = false;
        }
        return;
      }

      this.#authToken = data.token;
      sessionStorage.setItem('swf_checkin_token', data.token);
      this.#showMain();
      this.#loadEvents();
    } catch (err) {
      if (error) {
        error.textContent = 'Connection error. Please try again.';
        error.hidden = false;
      }
    }
  }

  #showMain() {
    if (this.refs.authScreen) this.refs.authScreen.hidden = true;
    if (this.refs.mainScreen) this.refs.mainScreen.hidden = false;
  }

  // ── Event loading ──

  async #loadEvents() {
    try {
      const res = await fetch(`${this.#apiUrl}/checkin/events`, {
        headers: { Authorization: `Bearer ${this.#authToken}` },
      });
      const data = await res.json();

      const select = this.refs.eventSelect;
      if (!select || !data.events) return;

      // Clear existing options except the placeholder
      while (select.options.length > 1) select.remove(1);

      for (const evt of data.events) {
        const opt = document.createElement('option');
        opt.value = evt.id;
        opt.textContent = `${evt.title} — ${evt.eventDate || ''} ${evt.eventTime || ''}`;
        select.appendChild(opt);
      }
    } catch (err) {
      console.error('Failed to load events:', err);
    }
  }

  // ── Event selection ──

  async handleEventChange(event) {
    const eventId = event.target.value;
    if (!eventId) {
      if (this.refs.searchWrap) this.refs.searchWrap.hidden = true;
      if (this.refs.attendeeList) this.refs.attendeeList.innerHTML = '';
      return;
    }

    this.#currentEventId = eventId;
    await this.#loadAttendees(eventId);
  }

  async #loadAttendees(eventId) {
    try {
      const res = await fetch(
        `${this.#apiUrl}/checkin/events/${encodeURIComponent(eventId)}/attendees`,
        { headers: { Authorization: `Bearer ${this.#authToken}` } }
      );
      const data = await res.json();

      this.#attendees = data.groups || [];
      this.#renderAttendees(this.#attendees);

      if (this.refs.searchWrap) this.refs.searchWrap.hidden = false;
      if (this.refs.searchInput) this.refs.searchInput.value = '';
      this.#updateStats();
    } catch (err) {
      console.error('Failed to load attendees:', err);
    }
  }

  // ── Rendering ──

  #renderAttendees(groups) {
    const list = this.refs.attendeeList;
    if (!list) return;

    if (groups.length === 0) {
      list.innerHTML = '<p style="text-align:center;color:#999;padding:2rem">No attendees found.</p>';
      return;
    }

    list.innerHTML = groups
      .map(
        (group) => `
        <div class="swf-checkin-group" data-order-id="${group.orderId}">
          <div class="swf-checkin-group__buyer">
            <span>${group.buyerName}</span>
            <span style="font-size:0.75rem;color:#999">${group.guests.length + 1} ticket${group.guests.length > 0 ? 's' : ''}</span>
          </div>
          ${this.#renderPerson(group.orderId, 0, group.buyerName, group.buyerEmail, group.buyerCheckedIn, false)}
          ${group.guests
            .map((g, i) =>
              this.#renderPerson(group.orderId, i + 1, g.name, g.email, g.checkedIn, true)
            )
            .join('')}
        </div>
      `
      )
      .join('');
  }

  #renderPerson(orderId, personIndex, name, email, checkedIn, isGuest) {
    const id = `${orderId}-${personIndex}`;
    return `
      <div class="swf-checkin-person${isGuest ? ' swf-checkin-person--guest' : ''}" data-name="${(name || '').toLowerCase()}">
        <input
          type="checkbox"
          class="swf-checkin-person__toggle"
          id="check-${id}"
          ${checkedIn ? 'checked' : ''}
          on:change="/handleToggleCheckin?orderId=${encodeURIComponent(orderId)}&personIndex=${personIndex}"
        >
        <div class="swf-checkin-person__info">
          <div class="swf-checkin-person__name">${name || 'Unknown'}</div>
          ${email ? `<div class="swf-checkin-person__email">${email}</div>` : ''}
        </div>
        <button type="button" class="swf-checkin-person__notes-btn"
          on:click="/toggleNotes?id=${id}"
        >Notes</button>
      </div>
      <div class="swf-checkin-notes" id="notes-${id}" hidden>
        <textarea placeholder="Add a note about this attendee..."></textarea>
        <button type="button" class="swf-checkin-notes__save"
          on:click="/saveNote?orderId=${encodeURIComponent(orderId)}&personIndex=${personIndex}&id=${id}"
        >Save Note</button>
      </div>
    `;
  }

  // ── Check-in toggle ──

  async handleToggleCheckin(data, event) {
    const checkbox = event.target;
    const { orderId, personIndex } = data;

    try {
      await fetch(`${this.#apiUrl}/checkin/toggle`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.#authToken}`,
        },
        body: JSON.stringify({
          orderId: decodeURIComponent(orderId),
          personIndex: parseInt(personIndex),
          checkedIn: checkbox.checked,
        }),
      });

      this.#updateStats();
    } catch (err) {
      // Revert on failure
      checkbox.checked = !checkbox.checked;
      console.error('Check-in toggle failed:', err);
    }
  }

  // ── Notes ──

  toggleNotes(data) {
    const notesDiv = document.getElementById(`notes-${data.id}`);
    if (notesDiv) notesDiv.hidden = !notesDiv.hidden;
  }

  async saveNote(data) {
    const { orderId, personIndex, id } = data;
    const notesDiv = document.getElementById(`notes-${id}`);
    const textarea = notesDiv?.querySelector('textarea');
    const note = textarea?.value?.trim();

    if (!note) return;

    try {
      await fetch(`${this.#apiUrl}/checkin/notes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.#authToken}`,
        },
        body: JSON.stringify({
          orderId: decodeURIComponent(orderId),
          personIndex: parseInt(personIndex),
          note,
        }),
      });

      if (notesDiv) notesDiv.hidden = true;
      if (textarea) textarea.value = '';
      alert('Note saved and emailed to info@snugwhiskeyfest.com');
    } catch (err) {
      alert('Failed to save note');
    }
  }

  // ── Search ──

  handleSearch(event) {
    const query = event.target.value.toLowerCase().trim();
    const people = this.refs.attendeeList?.querySelectorAll('.swf-checkin-person');
    const groups = this.refs.attendeeList?.querySelectorAll('.swf-checkin-group');

    if (!query) {
      // Show all
      groups?.forEach((g) => (g.style.display = ''));
      people?.forEach((p) => (p.style.display = ''));
      return;
    }

    // Hide groups that have no matching people
    groups?.forEach((group) => {
      const persons = group.querySelectorAll('.swf-checkin-person');
      let hasMatch = false;
      persons.forEach((p) => {
        const name = p.dataset.name || '';
        const match = name.includes(query);
        p.style.display = match ? '' : 'none';
        if (match) hasMatch = true;
      });
      group.style.display = hasMatch ? '' : 'none';
    });
  }

  // ── Stats ──

  #updateStats() {
    const stats = this.refs.stats;
    if (!stats) return;

    const checkboxes = this.refs.attendeeList?.querySelectorAll('.swf-checkin-person__toggle') || [];
    const total = checkboxes.length;
    const checkedIn = [...checkboxes].filter((cb) => cb.checked).length;

    stats.textContent = `${checkedIn} / ${total} checked in`;
  }
}

if (!customElements.get('swf-checkin-component')) {
  customElements.define('swf-checkin-component', SwfCheckinComponent);
}
